import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { createAccordionScope } from '@radix-ui/react-accordion';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { createContextScope } from '@radix-ui/react-context';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';
import { createCollectionIndex } from '../CollectionIndex/index.js';
import * as FacetValueListPrimitive from '../FacetValueList/index.js';
import { createContextFacetValueListScope } from '../FacetValueList/index.js';
/* -------------------------------------------------------------------------------------------------
 * AccordionFacets
 * ----------------------------------------------------------------------------------------------- */
const ACCORDION_FACETS_NAME = 'AccordionFacets';
// Create collection index to generate facet type index. It will be use when facet value is clicked
const [CollectionIndex, useCollectionIndex, createCollectionIndexScope] = createCollectionIndex(ACCORDION_FACETS_NAME);
const [createAccordionFacetsContext, createAccordionFacetsScope] = createContextScope(ACCORDION_FACETS_NAME, [
    createAccordionScope,
    createCollectionIndexScope,
    createContextFacetValueListScope,
]);
const useAccordionScope = createAccordionScope();
const useFacetListScope = createContextFacetValueListScope();
// Create Accordion provider to can share onclick function to the childrens
const [AccordionFacetsProvider, useAccordionFacetsContext] = createAccordionFacetsContext(ACCORDION_FACETS_NAME);
const AccordionFacets = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, defaultFacetTypesExpandedList, facetTypesExpandedList, onFacetTypesExpandedListChange, onFacetValueClick = () => { }, ...accordionProps } = props;
    const accordionScope = useAccordionScope(__scopeAccordionFacets);
    return (_jsx(CollectionIndex.Provider, { scope: __scopeAccordionFacets, children: _jsx(AccordionFacetsProvider, { scope: __scopeAccordionFacets, onFacetValueClick: onFacetValueClick, children: _jsx(CollectionIndex.Slot, { scope: __scopeAccordionFacets, children: _jsx(AccordionPrimitive.Root, { ...accordionScope, defaultValue: defaultFacetTypesExpandedList, onValueChange: onFacetTypesExpandedListChange, type: "multiple", ref: forwardedRef, value: facetTypesExpandedList, ...accordionProps }) }) }) }));
});
AccordionFacets.displayName = ACCORDION_FACETS_NAME;
/* -------------------------------------------------------------------------------------------------
 * AccordionFacet
 * ----------------------------------------------------------------------------------------------- */
const ACCORDION_FACET_NAME = 'AccordionFacet';
const [createAccordionFacetContext] = createContextScope(ACCORDION_FACET_NAME);
const [AccordionFacetProvider, useAccordionFacetContext] = createAccordionFacetContext(ACCORDION_FACET_NAME);
/**
 * `AccordionFacet` contains all of the parts of a collapsible section inside of an `Accordion`.
 */
const AccordionFacet = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, facetId, ...restProps } = props;
    const accordionScope = useAccordionScope(__scopeAccordionFacets);
    const ref = React.useRef(null);
    const composedRef = useComposedRefs(forwardedRef, ref);
    const getIndex = useCollectionIndex(ref, __scopeAccordionFacets);
    const { onFacetValueClick } = useAccordionFacetsContext(ACCORDION_FACET_NAME, __scopeAccordionFacets);
    const onFacetClickHandler = useCallbackRef(({ facetId, ...rest }) => {
        onFacetValueClick({
            facetId,
            facetIndex: getIndex(),
            ...rest,
        });
    });
    return (_jsx(AccordionFacetProvider, { scope: __scopeAccordionFacets, facetId: facetId, onFacetItemClick: onFacetClickHandler, children: _jsx(CollectionIndex.ItemSlot, { scope: __scopeAccordionFacets, children: _jsx(AccordionPrimitive.Item, { ...accordionScope, value: facetId, ...restProps, ref: composedRef }) }) }));
});
AccordionFacet.displayName = ACCORDION_FACET_NAME;
/* -------------------------------------------------------------------------------------------------
 * AccordionFacetContent
 * ----------------------------------------------------------------------------------------------- */
const ACCORDION_FACET_CONTENT_NAME = 'AccordionFacetContent';
/**
 * `AccordionFacetContent` contains all of the parts of a collapsible section inside of an `Accordion`.
 */
const AccordionFacetContent = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const accordionScope = useAccordionScope(__scopeAccordionFacets);
    return _jsx(AccordionPrimitive.Content, { ...accordionScope, ...restProps, ref: forwardedRef });
});
AccordionFacetContent.displayName = ACCORDION_FACET_CONTENT_NAME;
/* -------------------------------------------------------------------------------------------------
 * AccordionFacetValueList
 * ----------------------------------------------------------------------------------------------- */
const ACCORDION_FACET_VALUE_LIST_NAME = 'AccordionFacetValueList';
const AccordionFacetValueList = (props) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const facetListScope = useFacetListScope(__scopeAccordionFacets);
    const { facetId, onFacetItemClick } = useAccordionFacetContext(ACCORDION_FACET_VALUE_LIST_NAME, __scopeAccordionFacets);
    return (_jsx(FacetValueListPrimitive.Root, { ...facetListScope, ...restProps, facetId: facetId, onFacetClick: onFacetItemClick }));
};
AccordionFacetValueList.displayName = ACCORDION_FACET_VALUE_LIST_NAME;
const AccordionFacetValueListItem = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const facetListScope = useFacetListScope(__scopeAccordionFacets);
    return _jsx(FacetValueListPrimitive.Item, { ...facetListScope, ...restProps, ref: forwardedRef });
});
AccordionFacetValueListItem.displayName = 'AccordionFacetValueListItem';
const AccordionFacetValueListItemLabel = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const facetListScope = useFacetListScope(__scopeAccordionFacets);
    return _jsx(FacetValueListPrimitive.Label, { ...facetListScope, ...restProps, ref: forwardedRef });
});
AccordionFacetValueListItemLabel.displayName = 'AccordionFacetValueListItemLabel';
const AccordionFacetValueListItemCheckbox = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const facetListScope = useFacetListScope(__scopeAccordionFacets);
    return _jsx(FacetValueListPrimitive.Checkbox, { ...facetListScope, ...restProps, ref: forwardedRef });
});
AccordionFacetValueListItemCheckbox.displayName = 'AccordionFacetValueListItemCheckbox';
/* -------------------------------------------------------------------------------------------------
 * AccordionFacetValueListItemCheckboxIndicator
 * ----------------------------------------------------------------------------------------------- */
const AccordionFacetValueListItemCheckboxIndicator = FacetValueListPrimitive.CheckboxIndicator;
AccordionFacetValueListItemCheckboxIndicator.displayName = 'AccordionFacetValueListItemCheckboxIndicator';
const AccordionFacetValueListItemItemToggle = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const facetListScope = useFacetListScope(__scopeAccordionFacets);
    return _jsx(FacetValueListPrimitive.Toggle, { ...facetListScope, ...restProps, ref: forwardedRef });
});
AccordionFacetValueListItemItemToggle.displayName = 'AccordionFacetValueListItemItemToggle';
/* -------------------------------------------------------------------------------------------------
 * AccordionFacetHeader
 * ----------------------------------------------------------------------------------------------- */
const ACCORDION_FACET_HEADER_NAME = 'AccordionFacetHeader';
const AccordionFacetHeader = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const accordionScope = useAccordionScope(__scopeAccordionFacets);
    return _jsx(AccordionPrimitive.Header, { ...accordionScope, ...restProps, ref: forwardedRef });
});
AccordionFacetHeader.displayName = ACCORDION_FACET_HEADER_NAME;
/* -------------------------------------------------------------------------------------------------
 * AccordionFacetTrigger
 * ----------------------------------------------------------------------------------------------- */
const ACCORDION_FACET_TRIGGER_NAME = 'AccordionFacetTrigger';
const AccordionFacetTrigger = React.forwardRef((props, forwardedRef) => {
    const { __scopeAccordionFacets, ...restProps } = props;
    const accordionScope = useAccordionScope(__scopeAccordionFacets);
    return _jsx(AccordionPrimitive.AccordionTrigger, { ...accordionScope, ...restProps, ref: forwardedRef });
});
AccordionFacetTrigger.displayName = ACCORDION_FACET_TRIGGER_NAME;
const Root = AccordionFacets;
const Facet = AccordionFacet;
const Header = AccordionFacetHeader;
const Trigger = AccordionFacetTrigger;
const Content = AccordionFacetContent;
const ValueList = AccordionFacetValueList;
const Item = AccordionFacetValueListItem;
const ItemLabel = AccordionFacetValueListItemLabel;
const ItemCheckbox = AccordionFacetValueListItemCheckbox;
const ItemCheckboxIndicator = AccordionFacetValueListItemCheckboxIndicator;
const ItemToggle = AccordionFacetValueListItemItemToggle;
const useAccordionFacetsCollectionIndex = useCollectionIndex;
export { createAccordionFacetsContext, createAccordionFacetsScope, useAccordionFacetsCollectionIndex, useAccordionFacetContext, useAccordionFacetsContext, 
//
AccordionFacets, AccordionFacet, AccordionFacetHeader, AccordionFacetTrigger, AccordionFacetContent, AccordionFacetValueList, AccordionFacetValueListItem, AccordionFacetValueListItemLabel, AccordionFacetValueListItemCheckbox, AccordionFacetValueListItemCheckboxIndicator, AccordionFacetValueListItemItemToggle, 
//
Root, Facet, Header, Trigger, Content, ValueList, Item, ItemLabel, ItemCheckbox, ItemCheckboxIndicator, ItemToggle, };
